import { Banner } from "@/components/banner/banner.comp";
import { Faq } from "@/components/faq/faq.comp";
import { Layout } from "@/components/layout/layout.comp";
import { SupportedPlatforms } from "@/components/supportedPlatforms/supportedPlatforms.comp";
import { Testimonials } from "@/components/testimonials/testimonials.comp";
import { TrustedBy } from "@/components/trustedBy/trustedBy.comp";
import { WhyUseWidget } from "@/components/whyUseWidget/whyUseWidget.comp";
import { WidgetFeatures } from "@/components/widgetFeatures/widgetFeatures.comp";
import { WidgetHero } from "@/components/widgetHero/widgetHero.comp";
import { getDataFromDB } from "@/lib/mongodb";
import { IPage, IPlatform, IPlugin } from "@/types";
import { GetServerSidePropsContext } from "next";
import Script from "next/script";

export default function WidgetPerPlatfrom({
  app,
  platform,
  page,
}: {
  app: IPlugin;
  platform: IPlatform;
  page: IPage;
}) {

  if (!app.slug) {
    return <></>;
  }

  return (
    <Layout
      showHeader={true}
      showFooter={true}
      isDark
      title={page?.meta?.title || `${app.meta.title} Perfect for ${platform.title}.`}
      description={page?.meta?.description || `${app.meta.description} Works great with ${platform.title}.`}
      image={app.meta.imgUrl}
      serviceName={app.serviceName}
      url={`https://www.bracketsninja.com/platforms/${platform.slug}`}
    >
      <WidgetPlatformScript app={app} platform={platform} />
      <WidgetHero widget={app} platform={platform} />
      <WhyUseWidget widget={app} page={page} />
      <WidgetFeatures widget={app} page={page} />
      <TrustedBy />
      <Banner
        title={`Adding ${app.name} to ${platform.title}`}
        text={`Embedding the ${app.name} ${platform.appSynonym} in your ${platform.title} ${platform.siteSynonym} is simple and requires no special coding skills. Just copy a line of code to add the ${app.name} to your ${platform.siteSynonym}.`}
        buttonText={`For a Full Guide`}
        buttonLink={`/platforms/${platform.slug}/how-to-add`}
        bannerStyle={{ backgroundColor: '#FAFAFC', color: '#171B2C', border: '1px solid #EEEEEE' }}
      />
      <Testimonials mode="dark" />
      <Faq faqs={app.faq.map((faq) => faq)} />
      <SupportedPlatforms />
    </Layout>
  );
}

const WidgetPlatformScript = ({
  app,
  platform,
}: {
  app: IPlugin;
  platform: IPlatform;
}) => {
  let ratingValue: number = 4.8;
  if (app.slug === "brackets") {
    ratingValue = 4.9;
  }
  if (
    app.slug === "messenger-button" ||
    app.slug === "telegram-button" ||
    app.slug === "whatsapp-button"
  ) {
    ratingValue = 4.7;
  }

  function getRandomInt(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  return (
    <Script id="widget-platform-schema" type="application/ld+json">{`
          {
            "@context": "https://schema.org/", 
            "@type": "Product", 
            "name": "${app.name} for ${platform.title}",
            "image": "${app.hero}",
            "description": "${app.teaser.replace(/"/g, `'`)}",
            "aggregateRating": {
              "@type": "AggregateRating",
              "worstRating": "1",
              "bestRating": "5",
              "ratingCount": "${getRandomInt(1, 120) + 150}",
              "ratingValue": "${ratingValue}"
            }
          }
        `}</Script>
  );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { slug: platformSlug } = context.params || {};
  const [platform] = await getDataFromDB<IPlatform>('platforms', { slug: platformSlug, status: 'published' });
  const [app] = await getDataFromDB<IPlugin>('apps', { slug: 'brackets' });

  const [page] = await getDataFromDB<IPage>('pages', {
    appId: app?._id,
    platformId: platform?._id,
    status: 'published'
  });

  if (!app || !platform || !page) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }

  return {
    props: {
      app,
      platform,
      page: page || null,
    },
  };
}